import { Children } from 'react';
import { ICON_NAMES } from '@belong/types';
import { COLOURS } from '@belong/themes';

import { Container, Slides, Slide, Controls, Control } from './Carousel.styles';
import { Icon } from '../../Icon/Icon';
import { ICarouselProps } from './Carousel.types';
import { useViewModel } from './viewModel';
import { CarouselProgress } from './CarouselProgress';

export const Carousel: React.FC<ICarouselProps> = ({ children, initialSlide, 'data-testid': testId }) => {
  const {
    containerRef,
    slidesRef,
    currentSlide,
    totalSlides,
    totalColumns,
    slideColumns,
    decrementSlide,
    incrementSlide,
    getXTransition,
    getSlideOpacityTransition,
    getProgressWidth,
    handleKeydown
  } = useViewModel(children, initialSlide);

  return (
    <Container data-testid={testId} ref={containerRef}>
      <Controls>
        {currentSlide > 0 && (
          <Control
            type="button"
            onClick={decrementSlide}
            aria-label="previous slide"
            controlAction="decrease"
            role="button"
            className="carousel--control-decrease"
          >
            <Icon name={ICON_NAMES.ChevronLeft} hasColor={COLOURS.BELONG_BLUE} />
          </Control>
        )}
        {currentSlide < totalSlides - 1 && (
          <Control
            type="button"
            onClick={incrementSlide}
            aria-label="next slide"
            controlAction="increase"
            role="button"
            className="carousel--control-increase"
          >
            <Icon name={ICON_NAMES.ChevronRight} hasColor={COLOURS.BELONG_BLUE} />
          </Control>
        )}
      </Controls>
      <Slides
        cssAnimation={getXTransition()}
        draggable={false}
        ref={slidesRef}
        onKeyDownCapture={e => handleKeydown(e)}
        data-current-slide={currentSlide}
        className="carousel--slides"
      >
        {Children.map(children, (child, idx) => {
          return (
            <Slide
              totalColumns={totalColumns}
              visibleColumns={slideColumns}
              cssAnimation={getSlideOpacityTransition(idx)}
            >
              {child}
            </Slide>
          );
        })}
      </Slides>

      <CarouselProgress progressWidth={getProgressWidth()} currentSlide={currentSlide} totalSlides={totalSlides} />
    </Container>
  );
};

Carousel.displayName = 'Carousel';
